import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const SEOAudit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [auditUrl, setAuditUrl] = useState('');
  const [auditScope, setAuditScope] = useState('single_page');
  const [currentAudit, setCurrentAudit] = useState(null);
  const [epicCategories, setEpicCategories] = useState([]);
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditTasks, setAuditTasks] = useState([]);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [activeTab, setActiveTab] = useState('new-audit');

  useEffect(() => {
    loadData();
    // Cleanup polling on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const loadData = async () => {
    try {
      const [categoriesRes, historyRes] = await Promise.all([
        api.get('/seo-audit/epic-categories').catch(() => ({ data: { success: false } })),
        api.get('/seo-audit/audit-history').catch(() => ({ data: { success: false } }))
      ]);
      
      if (categoriesRes.data.success) {
        setEpicCategories(categoriesRes.data.epic_categories);
      }
      if (historyRes.data.success) {
        setAuditHistory(historyRes.data.audits);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const pollAuditStatus = async (auditId) => {
    try {
      const response = await api.get(`/seo-audit/audit-status/${auditId}`);
      if (response.data.success) {
        const data = response.data.audit;
        setAuditTasks(data.tasks || []);
        
        // If completed or failed, stop polling and load results
        if (data.status === 'completed' || data.status === 'failed') {
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          setLoading(false);
          
          if (data.status === 'completed') {
            setCurrentAudit(data.results);
            alert('SEO Audit completed successfully!');
            loadData();
            setActiveTab('results');
          } else {
            alert(`Audit failed: ${data.error || 'Unknown error'}`);
          }
        }
      }
    } catch (error) {
      console.error('Error polling audit status:', error);
    }
  };

  const startAudit = async (e) => {
    e.preventDefault();
    if (!auditUrl) return;

    try {
      setLoading(true);
      setAuditTasks([]);
      
      const response = await api.post('/seo-audit/start-audit', {
        url: auditUrl,
        scope: auditScope,
        focus_areas: []
      });
      
      if (response.data.success) {
        const auditId = response.data.audit_id;
        const tasks = response.data.tasks || [];
        
        setAuditTasks(tasks);
        
        // Start polling for status updates
        const interval = setInterval(() => pollAuditStatus(auditId), 2000);
        setPollingInterval(interval);
      }
    } catch (error) {
      console.error('Error starting audit:', error);
      alert(error.response?.data?.details || 'Failed to start SEO audit');
      setLoading(false);
    }
  };

  const handleViewAudit = (auditId) => {
    const audit = auditHistory.find(a => a.audit_id === auditId);
    if (audit) {
      setCurrentAudit(audit);
      setActiveTab('results');
    }
  };

  const getTaskStatusIcon = (status) => {
    switch(status) {
      case 'completed': return '✅';
      case 'running': return '⏳';
      case 'failed': return '❌';
      default: return '⏸️';
    }
  };

  const getTaskStatusColor = (status) => {
    switch(status) {
      case 'completed': return '#52c41a';
      case 'running': return '#1890ff';
      case 'failed': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    if (score >= 40) return '#fa8c16';
    return '#ff4d4f';
  };

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Home
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          SEO Audit & Optimization
        </span>
      </nav>

      {/* Page Title Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🔍 SEO Audit & Optimization
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Analyze your website's SEO performance and get actionable recommendations
            </p>
          </div>
        </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          borderBottom: '2px solid var(--gray-200)',
          gap: 'var(--space-2)'
        }}>
          {[
            { key: 'new-audit', label: '🔍 New Audit' },
            { key: 'results', label: '📊 Results', disabled: !currentAudit },
            { key: 'history', label: '📜 History' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => !tab.disabled && setActiveTab(tab.key)}
              disabled={tab.disabled}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                background: 'none',
                border: 'none',
                borderBottom: `3px solid ${activeTab === tab.key ? 'var(--primary-600)' : 'transparent'}`,
                cursor: tab.disabled ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                color: tab.disabled ? 'var(--gray-300)' : activeTab === tab.key ? 'var(--primary-600)' : 'var(--gray-500)',
                transition: 'all 0.3s',
                opacity: tab.disabled ? 0.5 : 1
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* New Audit Tab */}
      {activeTab === 'new-audit' && (
        <>
            <h3 style={{ marginTop: 0 }}>🚀 Start New SEO Audit</h3>
            
            <form onSubmit={startAudit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Website URL *
                </label>
                <input
                  type="url"
                  placeholder="https://your-website.com"
                  value={auditUrl}
                  onChange={e => setAuditUrl(e.target.value)}
                  required
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)', fontSize: '0.875rem' }}>
                  Audit Scope *
                </label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="single_page"
                      checked={auditScope === 'single_page'}
                      onChange={e => setAuditScope(e.target.value)}
                    />
                    📄 Single Page
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="full_website"
                      checked={auditScope === 'full_website'}
                      onChange={e => setAuditScope(e.target.value)}
                    />
                    🌐 Full Website
                  </label>
                </div>
              </div>
              
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !auditUrl}
                style={{ width: '100%' }}
              >
                {loading ? 'Analyzing...' : '🔍 Start SEO Audit'}
              </button>
            </form>

            {/* Task Progress Display */}
            {loading && auditTasks.length > 0 && (
              <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: 'var(--gray-50)', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>⚙️</span> Audit Progress
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {auditTasks.map((task, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '6px',
                      border: `2px solid ${getTaskStatusColor(task.status)}`
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>{getTaskStatusIcon(task.status)}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                          {task.emoji} {task.label}
                        </div>
                        {task.status === 'running' && (
                          <div style={{ 
                            height: '4px', 
                            background: 'var(--gray-200)', 
                            borderRadius: '2px',
                            overflow: 'hidden'
                          }}>
                            <div style={{ 
                              width: `${task.progress}%`, 
                              height: '100%', 
                              background: getTaskStatusColor(task.status),
                              transition: 'width 0.3s'
                            }} />
                          </div>
                        )}
                      </div>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        color: getTaskStatusColor(task.status),
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {task.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* SEO Categories - Moved below form */}
          {epicCategories.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3 style={{ marginTop: 0 }}>📋 SEO Categories</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                {epicCategories.slice(0, 5).map(category => (
                  <div key={category.id} style={{ padding: '0.75rem', background: 'var(--gray-50)', borderRadius: '8px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--primary-600)', marginBottom: '0.25rem' }}>{category.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>{category.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <>
          <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>📜 Audit History</h3>

          {auditHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3.75rem 1.25rem' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1.25rem' }}>📊</div>
              <h3>No Audit History</h3>
              <p style={{ color: 'var(--gray-600)' }}>Run your first SEO audit to see results here</p>
              <button className="btn btn-primary" onClick={() => setActiveTab('new-audit')} style={{ marginTop: '1rem' }}>
                Start First Audit
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {auditHistory.map(audit => (
                <div 
                  key={audit.audit_id} 
                  style={{ 
                    cursor: 'pointer',
                    padding: '1rem',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--gray-50)',
                    transition: 'all 0.2s'
                  }} 
                  onClick={() => {
                    setCurrentAudit(audit);
                    setActiveTab('results');
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-100)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-50)'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 0.5rem 0' }}>🌐 {audit.domain}</h3>
                      <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                        {new Date(audit.audit_date).toLocaleString()} • 
                        {audit.pages_analyzed} pages • 
                        {audit.total_issues} issues
                      </p>
                    </div>
                    <div style={{
                      width: '5rem',
                      height: '5rem',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: 'white',
                      background: getScoreColor(audit.overall_score)
                    }}>
                      {audit.overall_score?.toFixed(0) || 'N/A'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
};

export default SEOAudit;
